import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";

// Safe Markdown renderer for Sarge's assistant messages.
// - No raw HTML execution (react-markdown ignores raw HTML by default; rehype-sanitize enforces it).
// - External links open safely with rel="noopener noreferrer".
// - Styled for the dark command-center UI.
export function SargeMarkdown({ children }) {
  return (
    <div className="wr-markdown text-sm leading-relaxed" data-testid="sarge-markdown">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={{
          a: ({ node, ...props }) => (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              className="text-wr-red underline underline-offset-2 hover:text-white break-words"
            />
          ),
          h1: ({ node, ...props }) => (
            <h1 {...props} className="wr-heading text-white text-lg font-bold mt-3 mb-2 first:mt-0" />
          ),
          h2: ({ node, ...props }) => (
            <h2 {...props} className="wr-heading text-white text-base font-bold mt-3 mb-2 first:mt-0" />
          ),
          h3: ({ node, ...props }) => (
            <h3 {...props} className="wr-heading text-white text-sm font-semibold mt-3 mb-1.5 first:mt-0 uppercase tracking-wide" />
          ),
          p: ({ node, ...props }) => <p {...props} className="my-2 first:mt-0 last:mb-0 text-zinc-100" />,
          strong: ({ node, ...props }) => <strong {...props} className="font-semibold text-white" />,
          em: ({ node, ...props }) => <em {...props} className="italic text-zinc-200" />,
          ul: ({ node, ...props }) => <ul {...props} className="list-disc pl-5 my-2 space-y-1 marker:text-wr-red" />,
          ol: ({ node, ...props }) => <ol {...props} className="list-decimal pl-5 my-2 space-y-1 marker:text-wr-red" />,
          li: ({ node, ...props }) => <li {...props} className="text-zinc-100" />,
          blockquote: ({ node, ...props }) => (
            <blockquote {...props} className="border-l-2 border-wr-red pl-3 my-2 text-zinc-300 italic" />
          ),
          code: ({ node, inline, className, children, ...props }) =>
            inline ? (
              <code {...props} className="wr-mono text-[0.82em] bg-wr-bg border border-wr-border px-1.5 py-0.5 rounded-sm text-zinc-100">
                {children}
              </code>
            ) : (
              <code {...props} className="wr-mono text-[0.82em] text-zinc-100">
                {children}
              </code>
            ),
          pre: ({ node, ...props }) => (
            <pre
              {...props}
              className="wr-mono text-xs bg-wr-bg border border-wr-border p-3 rounded-sm my-2 overflow-x-auto"
            />
          ),
          hr: ({ node, ...props }) => <hr {...props} className="my-3 border-wr-border" />,
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-2">
              <table {...props} className="w-full text-xs border border-wr-border" />
            </div>
          ),
          th: ({ node, ...props }) => (
            <th {...props} className="border border-wr-border px-2 py-1 text-left wr-label !text-[0.62rem]" />
          ),
          td: ({ node, ...props }) => <td {...props} className="border border-wr-border px-2 py-1" />,
        }}
      >
        {children || ""}
      </ReactMarkdown>
    </div>
  );
}
